import { ReactNode } from 'react';

interface ChartPanelProps {
  title: string;
  icon: ReactNode;
  iconClassName: string;
  subtitle?: ReactNode;
  summary?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * Stretch to the parent's height so panels in a grid row line up. Disable
   * for panels stacked directly in a page, where `h-full` would resolve to the
   * whole page's height.
   */
  fillHeight?: boolean;
}

export function ChartPanel({
  title,
  icon,
  iconClassName,
  subtitle,
  summary,
  footer,
  children,
  className = '',
  fillHeight = true,
}: ChartPanelProps) {
  return (
    <section
      className={`flex ${fillHeight ? 'h-full' : ''} flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft ${className}`}
    >
      <div className="flex min-h-14 flex-shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
        <span
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full [&>svg]:h-4 [&>svg]:w-4 ${iconClassName}`}
        >
          {icon}
        </span>
        <div className="min-w-0">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-900">{title}</h2>
          {subtitle && <div className="mt-0.5 text-[11px] text-slate-400">{subtitle}</div>}
        </div>
        {summary && <div className="ml-auto">{summary}</div>}
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
        {children}
      </div>
      {footer && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2 sm:px-5">
          {footer}
        </div>
      )}
    </section>
  );
}

export function ChartEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-36 flex-1 items-center justify-center text-center text-sm text-slate-400">
      {children}
    </div>
  );
}

interface ChartBarGradientProps {
  id: string;
  color: string;
  lightColor?: string;
}

export function ChartBarGradient({ id, color, lightColor }: ChartBarGradientProps) {
  return (
    <linearGradient id={id} x1="0" y1="100%" x2="0" y2="0%">
      <stop offset="0%" stopColor={lightColor ?? color} stopOpacity={lightColor ? 1 : 0.45} />
      <stop offset="100%" stopColor={color} />
    </linearGradient>
  );
}

export function ChartSliceGradient({ id, color, lightColor }: ChartBarGradientProps) {
  return (
    <linearGradient id={id} x1="0" y1="100%" x2="100%" y2="0">
      <stop offset="0%" stopColor={lightColor ?? color} stopOpacity={lightColor ? 1 : 0.45} />
      <stop offset="100%" stopColor={color} />
    </linearGradient>
  );
}
